import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Minus, Crown, Lock, Sparkles, UserCheck, ArrowRight } from 'lucide-react';
import { useAuthStore, useSubscriptionStore } from '@shared/state';
import { startCheckout, getSubscription, onSubscriptionChange } from '@shared/services/subscription';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import {
  Aurora, MagneticCard, GlowBorder, SectionDivider, Reveal, StaggerGroup,
} from '../components/ui/depth';
import { scaleItem } from '../components/ui/depth-motion';
import { Footer } from '../components/Footer';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';

const pricingFaq = [
  { question: 'Can I try CogniPrint for free?', answer: 'Yes. The quick CogniPrint diagnostic (2 minutes) and your 4-dimension profile are free, always. You\'ll see exactly how CogniPrint maps your learning profile before you pay anything. Upgrade to access all 8 dimensions and the full platform features.' },
  { question: 'Can I cancel anytime?', answer: 'Yes. Monthly subscriptions can be cancelled at any time from your account settings. No contracts, no penalties.' },
  { question: 'What happens if I use all my included uploads?', answer: 'Free accounts include one lifetime upload and three lifetime Tutor queries. Learner includes one upload per month with additional uploads at $2.99 each and 20 Tutor queries per month. Pro includes five uploads per month with additional uploads at $1.99 each and unlimited Tutor queries. Teams inherits Pro\'s allowances per seat. Your dashboard shows exactly how many uploads you have used and how many remain.' },
  { question: 'Do you offer annual plans or student discounts?', answer: 'Annual plans and student pricing are on our roadmap. Sign up for updates and we\'ll let you know when they\'re available.' },
  { question: 'What is the Coach add-on and how does it differ from the AI Tutor?', answer: 'The AI Tutor is always on — unlimited, 24/7, shaped by your CogniPrint. The Coach add-on ($7.99/month on Pro) adds a human in the loop: bi-weekly written recalibration reviews plus unlimited ad-hoc written check-ins. It does not replace the AI Tutor; it sits on top of it.' },
  { question: 'Do I automatically get audio, voice Tutor, knowledge graph, and gamification when they launch?', answer: 'Yes. These features roll out as part of Phase 2 development and are included at the tier where they live — Pro and Teams for the full suite, Learner for a preview. We will tell you when each one ships via the in-product inbox and your monthly report.' },
  { question: 'Can I try the Coach add-on for a month?', answer: 'Yes. The Coach add-on is monthly and can be cancelled from your account settings at any time. Add it the month before a specific exam and drop it when you no longer need it.' },
  { question: 'What is the Day-14 Learner offer and who gets it?', answer: 'Free users who complete the 90-second Confidence Preview at Day 14 receive a one-time offer: Learner tier at $5.99/month for three months (instead of $9.99/month). The offer is time-bound — redeemable within seven days of Confidence Preview completion. After the three discounted months, Learner reverts to the standard $9.99/month price.' },
  { question: 'What does the Free tier actually include?', answer: 'A quick CogniPrint profile across four of the eight dimensions, one upload for the lifetime of the account, three Tutor queries for the lifetime of the account, and access to the learning interface. On recalibration, Free users get the 90-second Confidence Preview at Day 14.' },
  { question: 'How does Coach work on Teams?', answer: 'The Teams admin purchases a pool of Coach seats at $7.99 per assigned seat per month, then assigns those seats to specific team members. Team members cannot self-purchase Coach on Teams — assignment is admin-controlled so the budget stays predictable. A Teams seat plus Coach assignment comes to $27.98 per month, within one cent of solo Pro with Coach at $27.99.' },
  { question: 'What is the Confidence Preview versus the Confidence Check?', answer: 'The Confidence Preview is a 90-second, three-question snapshot shown to Free users at Day 14 — a headline output. The Confidence Check is the full Day-14 recalibration included on Learner, Pro, and Teams: a detailed view of how your CogniPrint has moved, a monthly report, exam countdown, and T-7 / T-1 reminders.' },
];

interface TierFeature {
  label: string;
  free: string | boolean;
  learner: string | boolean;
  pro: string | boolean;
  teams: string | boolean;
  emphasis?: boolean;
}

const features: TierFeature[] = [
  { label: 'CogniPrint Profile', free: '4 dimensions', learner: '8 dimensions', pro: '8 dimensions', teams: '8 dimensions', emphasis: true },
  { label: 'Included Uploads', free: '1 (lifetime)', learner: '1/month', pro: '5/month', teams: '5/seat/month' },
  { label: 'Additional Uploads', free: false, learner: '$2.99 each', pro: '$1.99 each', teams: '$1.49 each' },
  { label: 'Professor Cogni (AI Tutor)', free: '3 lifetime', learner: '20/mo', pro: 'Unlimited', teams: 'Unlimited' },
  { label: 'Bi-Weekly Recalibration', free: 'Confidence Preview', learner: 'Confidence Check', pro: 'Unlimited within cooldown', teams: 'Per seat', emphasis: true },
  { label: 'Monthly Report', free: false, learner: true, pro: true, teams: true },
  { label: 'Audio Ingestion & Voice Tutor', free: false, learner: 'Preview', pro: true, teams: true },
  { label: 'Visual Knowledge Graph', free: false, learner: 'Preview', pro: true, teams: true },
  { label: 'Gamification (motivation-aware)', free: true, learner: true, pro: true, teams: true },
  { label: 'Cohort Leaderboards', free: false, learner: false, pro: false, teams: true },
  { label: 'Coach Add-On Eligible', free: false, learner: false, pro: '+$7.99/mo', teams: '+$7.99/seat', emphasis: true },
  { label: 'Priority Processing', free: false, learner: false, pro: true, teams: true },
  { label: 'Exports', free: false, learner: 'PDF map', pro: 'All formats', teams: 'All + team reports' },
];

function FeatureValue({ value }: { value: string | boolean }) {
  if (value === true) return <Check size={15} className="text-success mx-auto" />;
  if (value === false) return <Minus size={15} className="text-content-dim mx-auto" />;
  return <span className="text-xs">{value}</span>;
}

/* ─── HERO ─── */
function Hero() {
  return (
    <section className="relative pt-4 pb-12 px-4 text-center overflow-hidden">
      <Aurora palette="mixed" intensity={0.9} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal>
          <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
            <Sparkles size={12} /> Always a free tier
          </span>
        </Reveal>
        <Reveal>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-5 leading-[1.1]">
            Plans that grow with{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">how you learn.</span>
          </h1>
        </Reveal>
        <Reveal>
          <p className="text-content-secondary font-body text-lg leading-relaxed">
            Your quick CogniPrint profile is free — always. The 2-minute diagnostic costs nothing and gives you 4 core dimensions. Upgrade to unlock all 8 and choose a plan when you're ready.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── DAY-14 BANNER ─── */
function Day14Banner() {
  return (
    <section className="relative px-4 -mt-4">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <div className="relative rounded-xl p-[1px] overflow-hidden bg-gradient-to-r from-gold-500/40 via-indigo-500/40 to-gold-500/40 animate-gradient-flow">
            <div className="rounded-[11px] bg-dark-bg/90 backdrop-blur-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 relative overflow-hidden">
              <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 4 }}
                style={{ background: 'linear-gradient(90deg, transparent, rgba(240,165,0,0.12), transparent)', width: '40%' }}
              />
              <span className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-pill text-[10px] font-semibold bg-gold-500/15 text-gold-400 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                DAY-14 OFFER
              </span>
              <p className="text-sm font-body text-content-secondary flex-1">
                Free users who complete the 90-second Confidence Preview unlock Learner at <span className="text-gold-400 font-semibold">$5.99/month for three months</span> (then $9.99/mo). One-time, redeemable within 7 days.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── TIER CARDS ─── */
type Tier = 'learner' | 'pro' | 'teams';
type TierCardProps = {
  tier: Tier | 'free';
  name: string;
  price: string;
  priceUnit: string;
  zar?: string;
  blurb: string;
  highlights: string[];
  cta: React.ReactNode;
  highlighted?: boolean;
};

function TierCard({ tier, name, price, priceUnit, zar, blurb, highlights, cta, highlighted }: TierCardProps) {
  const cardInner = (
    <div
      className={`relative h-full rounded-2xl p-6 sm:p-7 border ${highlighted
        ? 'border-gold-500/45 ring-1 ring-gold-500/30 shadow-glow-gold'
        : 'border-indigo-400/15'
      }`}
      style={THICK_TILE_STYLE}
    >
      {highlighted ? (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-pill text-xs font-semibold bg-gradient-to-r from-indigo-500 to-indigo-600 text-white flex items-center gap-1 shadow-glow-indigo">
          <Crown size={12} /> Most Popular
        </span>
      ) : null}

      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-heading font-semibold text-lg">{name}</h3>
        {tier === 'free' ? (
          <span className="text-[10px] font-mono text-content-muted">forever</span>
        ) : null}
      </div>
      <p className="text-xs font-body text-content-muted leading-relaxed mb-4">{blurb}</p>
      <div className="flex items-baseline gap-1 mb-1">
        <span className={`font-heading font-extrabold ${highlighted ? 'text-4xl bg-gradient-to-r from-indigo-300 to-gold-400 bg-clip-text text-transparent' : 'text-3xl'}`}>{price}</span>
        <span className="text-content-muted text-sm font-body">{priceUnit}</span>
      </div>
      {zar ? <p className="text-content-dim text-xs font-body mb-5">{zar}</p> : <div className="mb-5" />}

      <ul className="space-y-2.5 mb-7 text-sm font-body">
        {highlights.map((h) => (
          <li key={h} className="flex items-start gap-2 text-content-secondary">
            <Check size={14} className="text-success mt-0.5 shrink-0" />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {cta}
    </div>
  );

  return highlighted ? (
    <MagneticCard glow="indigo" intensity={5}>{cardInner}</MagneticCard>
  ) : (
    <MagneticCard glow="mixed" intensity={4}>{cardInner}</MagneticCard>
  );
}

function TierGrid({
  currentTier,
  upgrading,
  onUpgrade,
}: {
  currentTier: string;
  upgrading: string | null;
  onUpgrade: (tier: Tier) => void;
}) {
  const isCurrent = (t: string) => currentTier === t;
  return (
    <section className="relative py-12 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto relative">
        <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          <motion.div variants={scaleItem}>
            <TierCard
              tier="free"
              name="Free"
              price="$0"
              priceUnit=""
              blurb="See exactly what CogniPrint knows about you before you pay anything."
              highlights={[
                '4-dimension CogniPrint profile',
                '1 upload (lifetime, 10 pages max)',
                '3 Professor Cogni queries (lifetime)',
                'Day-14 Confidence Preview',
              ]}
              cta={isCurrent('free') ? (
                <span className="block w-full text-center py-2.5 rounded-lg bg-dark-border text-sm font-body text-content-muted">Current Plan</span>
              ) : (
                <Link to="/signup" className="block">
                  <Button variant="ghost" className="w-full">Get Started — Free</Button>
                </Link>
              )}
            />
          </motion.div>

          <motion.div variants={scaleItem}>
            <TierCard
              tier="learner"
              name="Learner"
              price="$9.99"
              priceUnit="/mo"
              zar="~R170/mo"
              blurb="Your full 8-dimension profile, monthly recalibration, and Professor Cogni for serious study."
              highlights={[
                'Full 8-dimension CogniPrint profile',
                '1 upload/month (+$2.99 extras)',
                '20 Tutor queries/month',
                'Day-14 Confidence Check + monthly report',
                'Audio & knowledge graph (preview)',
              ]}
              cta={
                <Button
                  className="w-full"
                  onClick={() => onUpgrade('learner')}
                  loading={upgrading === 'learner'}
                  disabled={isCurrent('learner')}
                >
                  {isCurrent('learner') ? <><Check size={16} /> Current Plan</> : 'Start with Learner'}
                </Button>
              }
            />
          </motion.div>

          <motion.div variants={scaleItem}>
            <TierCard
              tier="pro"
              name="Pro"
              price="$27.99"
              priceUnit="/mo"
              zar="~R475/mo · less than R110/week"
              blurb="The full platform. Unlimited Tutor, recalibration, audio, voice, knowledge graph, gamification."
              highlights={[
                'Everything in Learner',
                '5 uploads/month (+$1.99 extras)',
                'Unlimited Professor Cogni queries',
                'Unlimited recalibrations within cooldown',
                'Full audio, voice Tutor, knowledge graph',
                'Coach add-on eligible (+$7.99/mo)',
              ]}
              highlighted
              cta={
                <Button
                  className="w-full shadow-glow-indigo"
                  size="lg"
                  onClick={() => onUpgrade('pro')}
                  loading={upgrading === 'pro'}
                  disabled={isCurrent('pro')}
                >
                  {isCurrent('pro') ? <><Check size={16} /> Current Plan</> : <><Lock size={16} /> Go Pro</>}
                </Button>
              }
            />
          </motion.div>

          <motion.div variants={scaleItem}>
            <TierCard
              tier="teams"
              name="Teams"
              price="$19.99"
              priceUnit="/seat/mo"
              zar="Per-seat · cohort features"
              blurb="Per-seat Pro features plus cohort leaderboards, team reports, and a shared Coach seat pool."
              highlights={[
                'Everything in Pro, per seat',
                'Cohort leaderboards (weekly)',
                'Instructor view: aggregate mastery',
                'Cohort challenges',
                'Coach seat pool (admin-assigned)',
              ]}
              cta={
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => onUpgrade('teams')}
                  loading={upgrading === 'teams'}
                >
                  Book a walkthrough
                </Button>
              }
            />
          </motion.div>
        </StaggerGroup>

      </div>
    </section>
  );
}

/* ─── COACH ADD-ON ─── */
function CoachSection() {
  return (
    <section className="relative py-12 px-4 overflow-hidden">
      <Aurora palette="gold" intensity={0.5} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal variants={scaleItem}>
          <GlowBorder
            rounded="rounded-2xl"
            className="border border-gold-500/25 p-6 md:p-9 relative overflow-hidden"
            style={THICK_TILE_STYLE}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shrink-0 shadow-glow-gold">
                <UserCheck size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-3 mb-1">
                  <h3 className="font-heading font-semibold text-2xl">Coach Add-On</h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-xs font-semibold bg-gold-500/15 text-gold-400">Stacks on Pro or Teams</span>
                </div>
                <p className="text-content-secondary font-body leading-relaxed">
                  A human in the loop on top of the AI. Professor Cogni stays unlimited and 24/7 — Coach turns recalibration into a bi-weekly written conversation tied to your CogniPrint.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <MagneticCard glow="gold" intensity={4}>
                <div
                  className="relative overflow-hidden rounded-xl border border-gold-500/20 p-5 h-full"
                  style={THICK_TILE_STYLE}
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-heading font-semibold text-lg">Solo Coach</span>
                    <span className="text-content-muted text-xs font-body">on Pro</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-heading font-extrabold text-3xl text-gold-400">+$7.99</span>
                    <span className="text-content-muted text-sm font-body">/mo on top of Pro</span>
                  </div>
                  <ul className="space-y-2 text-sm font-body text-content-secondary">
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Bi-weekly recalibration cadence (7-day cooldown)</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Unlimited ad-hoc written check-ins</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Opus 4.7 priority routing for the Coach</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Counterfactual simulator</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Auto-pilot mode with review flags</li>
                  </ul>
                </div>
              </MagneticCard>

              <MagneticCard glow="gold" intensity={4}>
                <div
                  className="relative overflow-hidden rounded-xl border border-gold-500/20 p-5 h-full"
                  style={THICK_TILE_STYLE}
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-heading font-semibold text-lg">Teams Coach</span>
                    <span className="text-content-muted text-xs font-body">seat pool</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-heading font-extrabold text-3xl text-gold-400">+$7.99</span>
                    <span className="text-content-muted text-sm font-body">/assigned seat/mo</span>
                  </div>
                  <ul className="space-y-2 text-sm font-body text-content-secondary">
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Same Coach features as Solo</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Admin purchases & assigns the pool</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> Predictable budget — no member self-purchase</li>
                    <li className="flex gap-2"><Check size={14} className="text-success shrink-0 mt-0.5" /> $19.99 + $7.99 = $27.98 (parity with solo Pro + Coach)</li>
                  </ul>
                </div>
              </MagneticCard>
            </div>

            <p className="text-content-muted text-xs font-body mt-5 text-center">
              Coach requires Pro (for solo) or Teams (for seat-pool). Not available on Free or Learner.
            </p>
          </GlowBorder>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── FEATURE MATRIX ─── */
function MatrixSection() {
  return (
    <section className="relative py-16 px-4 overflow-hidden">
      <Aurora palette="cool" intensity={0.4} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal className="text-center mb-10">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">Compare every feature</h2>
          <p className="text-content-secondary font-body">Side-by-side, no asterisks.</p>
        </Reveal>

        <Reveal>
          <div
            className="relative rounded-2xl border border-indigo-400/15 overflow-hidden"
            style={THICK_TILE_STYLE}
          >
            <div className="grid grid-cols-5 text-xs font-body font-bold tracking-wider uppercase text-content-muted bg-dark-bg/40 border-b border-dark-border">
              <div className="px-4 py-3">Feature</div>
              <div className="px-4 py-3 text-center">Free</div>
              <div className="px-4 py-3 text-center">Learner</div>
              <div className="px-4 py-3 text-center text-indigo-300 bg-indigo-500/8 border-x border-indigo-500/20 relative">
                <span className="inline-flex items-center gap-1"><Crown size={11} /> Pro</span>
              </div>
              <div className="px-4 py-3 text-center">Teams</div>
            </div>
            {features.map((f, i) => (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`grid grid-cols-5 text-sm font-body border-t border-dark-border hover:bg-dark-surface/40 transition-colors ${f.emphasis ? 'bg-gold-500/[0.04]' : ''}`}
              >
                <div className={`px-4 py-3 ${f.emphasis ? 'text-content font-semibold' : 'text-content-secondary'} flex items-center gap-2`}>
                  {f.emphasis ? <Sparkles size={11} className="text-gold-400 shrink-0" /> : null}
                  {f.label}
                </div>
                <div className="px-4 py-3 text-center text-content-muted"><FeatureValue value={f.free} /></div>
                <div className="px-4 py-3 text-center text-content-secondary"><FeatureValue value={f.learner} /></div>
                <div className="px-4 py-3 text-center text-content bg-indigo-500/[0.05] border-x border-indigo-500/20"><FeatureValue value={f.pro} /></div>
                <div className="px-4 py-3 text-center text-content-secondary"><FeatureValue value={f.teams} /></div>
              </motion.div>
            ))}

            {/* Closer row — mirrors the header band and surfaces a Start-free CTA so
               the table doesn't terminate flush. */}
            <div className="grid grid-cols-5 text-sm font-body text-content-secondary bg-dark-bg/40 border-t border-dark-border">
              <div className="col-span-5 px-4 py-4 flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1.5 text-center">
                <span>Every plan includes the free 4-dimension profile and the 2-minute diagnostic.</span>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-1 font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Start free <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── VALUE COMPARISON GRAPHIC ─── */
function ValueComparisonSection() {
  const items = [
    { label: 'CogniPrint Pro', sublabel: '5-month exam prep · personalised', value: 139.95, max: 1000, tone: 'win' as const, accent: 'Includes recalibration, monthly report, audio, voice, knowledge graph, gamification' },
    { label: 'Pro + Coach', sublabel: '5-month exam prep · with human in the loop', value: 179.9, max: 1000, tone: 'win' as const, accent: 'Bi-weekly written reviews and unlimited check-ins on top of the AI' },
    { label: 'Weekly private tutor', sublabel: '5-month exam prep · 1 hour / week', value: 900, max: 1000, tone: 'lose' as const, accent: 'Generic, schedule-bound, no profile' },
  ];
  return (
    <section className="relative py-16 px-4 overflow-hidden">
      <Aurora palette="indigo" intensity={0.4} />
      <div className="max-w-5xl mx-auto relative">
        <Reveal className="text-center mb-10 max-w-2xl mx-auto">
          <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
            What it actually costs
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3 leading-tight">
            Less than a single hour of private tutoring{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">— per month.</span>
          </h2>
          <p className="text-content-secondary font-body">
            Five months of exam preparation, side by side. SA market rates for private tutoring.
          </p>
        </Reveal>

        <Reveal>
          <div
            className="relative overflow-hidden rounded-2xl border border-indigo-400/15 p-6 sm:p-8 space-y-5"
            style={THICK_TILE_STYLE}
          >
            {items.map((row, i) => (
              <div key={row.label}>
                <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
                  <div>
                    <div className={`font-heading font-semibold ${row.tone === 'win' ? 'text-content' : 'text-content-muted'}`}>{row.label}</div>
                    <div className="text-xs font-body text-content-muted">{row.sublabel}</div>
                  </div>
                  <div className={`font-heading font-extrabold text-2xl ${row.tone === 'win' ? 'bg-gradient-to-r from-indigo-300 to-gold-400 bg-clip-text text-transparent' : 'text-error/80'}`}>
                    ${row.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="h-3 rounded-full bg-dark-bg/70 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(row.value / row.max) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, delay: 0.1 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className={`h-full rounded-full ${row.tone === 'win'
                      ? 'bg-gradient-to-r from-indigo-500 to-gold-500'
                      : 'bg-gradient-to-r from-error/70 to-error/40'
                    }`}
                  />
                </div>
                <div className="text-[11px] font-body text-content-muted mt-1.5">{row.accent}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-5 text-center">
          <p className="text-content-secondary font-body text-sm">
            If cost is the friction: the free 2-minute Confidence Preview and free 4-dimension profile cost nothing. <Link to="/signup" className="text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center gap-0.5">Start there <ArrowRight size={12} /></Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FaqSection() {
  return (
    <section className="relative py-16 px-4 overflow-hidden">
      <Aurora palette="indigo" intensity={0.4} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal className="text-center mb-10">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl">Pricing FAQ</h2>
        </Reveal>
        <Reveal>
          <Accordion items={pricingFaq} />
        </Reveal>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCta() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <RidgeTexture opacity="opacity-[0.07]" />
      <Aurora palette="mixed" intensity={0.9} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal variants={scaleItem}>
          <GlowBorder rounded="rounded-2xl" className="bg-dark-surface/70 backdrop-blur-md p-8 sm:p-12 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-6 leading-tight">
              Start with your free CogniPrint profile.
            </h2>
            <p className="text-content-secondary font-body mb-8 max-w-xl mx-auto">
              2 minutes. 4 dimensions. No credit card. Upgrade only when you're ready.
            </p>
            <Link to="/signup" className="inline-block">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                <Button size="lg">Get Started — Free <ArrowRight size={16} /></Button>
              </motion.div>
            </Link>
          </GlowBorder>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── PAGE ─── */
export function PricingPage() {
  const user = useAuthStore((s) => s.user);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const setSubscription = useSubscriptionStore((s) => s.setSubscription);
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    getSubscription(user.uid).then((sub) => setSubscription(sub));
    const unsubscribe = onSubscriptionChange(user.uid, (sub) => setSubscription(sub));
    return () => unsubscribe?.();
  }, [user, setSubscription]);

  useEffect(() => {
    window.LemonSqueezy?.Setup({
      eventHandler: (event) => {
        if (event.event === 'Checkout.Success') navigate('/pricing/success');
      },
    });
  }, [navigate]);

  const currentTier = subscription?.tier ?? user?.subscription ?? 'free';

  async function handleUpgrade(tier: Tier) {
    if (!user) {
      navigate('/signup');
      return;
    }
    if (tier === 'teams') {
      navigate('/for-teams');
      return;
    }
    setUpgrading(tier);
    trackEvent({
      name: ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADE_STARTED,
      properties: {
        ...getBaseProperties(),
        tier: currentTier,
        fromTier: currentTier,
        toTier: tier,
        trigger: 'pricing_page',
      },
    });
    try {
      const { checkoutUrl } = await startCheckout({ userId: user.uid, userEmail: user.email, tier });
      if (window.LemonSqueezy) {
        window.LemonSqueezy.Url.Open(checkoutUrl);
      } else {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      console.error('Checkout failed:', err);
    } finally {
      setUpgrading(null);
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Hero />
      <SectionDivider />
      <TierGrid currentTier={currentTier} upgrading={upgrading} onUpgrade={handleUpgrade} />
      <Day14Banner />
      <SectionDivider />
      <CoachSection />
      <SectionDivider />
      <MatrixSection />
      <SectionDivider />
      <ValueComparisonSection />
      <SectionDivider />
      <FaqSection />
      <SectionDivider />
      <FinalCta />
      <Footer />
    </div>
  );
}
